/**
 * B397 leg (3) — the merged CART & PAY page as ONE screen (73:2,
 * redline-storefront-cart-pay-390.md): the cart card (lines · promo ·
 * total), the pay card (provider fields · email · trust footnote) and the
 * sticky PAY carrying the total — the LATE moment (B395's rule worn by the
 * buyer's page: no order exists until PAY, so the fields are Stripe's
 * deferred-intent element quoted from the DISPLAY total).
 *
 * Honesty seams, each from a read or absent (§10):
 *  - the HOLD chip and the "seats stay held" trust clause render ONLY over
 *    a live hold (pre-PAY there is none — nothing is claimed);
 *  - the "never through us" trust clause renders ONLY while the provider's
 *    tokenised element is actually the thing on the page;
 *  - an unavailable gateway walls PAY with its named reason (R14);
 *  - the authorized face retires fields AND door: the provider holds the
 *    money, the kernel confirms via webhook, the poller moves the page.
 *
 * Recorded deviations (frame-faithful, each with a way back):
 *  - each line wears B391's stepper though 73:2 draws a read-only
 *    sentence — B391 is a Pathik RULING ("the storefront cart wears the
 *    same rule") and a ruling outranks a frame until re-ruled; the
 *    sentence itself stays the frame's. A ghost Remove door per line
 *    stays too (R14: a trapped buyer is worse than a ghost door).
 *  - a '‹ Keep shopping' way back stands above the cards (R14, the same
 *    recorded deviation as the tickets step's way-back).
 */
import { useState, type ReactElement, type ReactNode } from "react";
import { Banner, Button, CartLineCard, Checkbox, EmptyState, Input, LabelledRule, PaymentWait, SheetCard, SheetFoot, ShowSummary, StickyBar, Stepper as UiStepper } from "@transact/ui";
import type { ShowHeadFace } from "../lib/showTickets.js";
import { formatMoney } from "../lib/money.js";
import { busyWords, cartActKey, doorBusy } from "../lib/busyFace.js";
import { holdWords, type CartCardLine, type ChargeRow } from "../lib/cartPayFace.js";
import type { Money } from "../lib/money.js";

export interface CartAdjustmentFace {
  readonly promotionId: string;
  readonly label: string;
  readonly amount: Money;
}

export function CartPayView(props: {
  readonly lines: readonly CartCardLine[];
  readonly total?: Money | undefined;
  /** B170 (§10.2): every fired promotion renders as a named row. */
  readonly adjustments?: readonly CartAdjustmentFace[];
  /** B629 (c): each fee the buyer pays, then Tax — drawn before the Total, so the sum reads down to it. */
  readonly charges?: readonly ChargeRow[];
  readonly promoApplied: boolean;
  readonly promoRefusal?: string | undefined;
  readonly onApplyPromo: (code: string) => void;
  /** Milliseconds left on the seat hold; absent/≤0 = no hold, no chip (§10). */
  readonly holdMsLeft?: number;
  /** B415 (b): the booking's name — REQUIRED; the App refuses the pay press in words without it (B407 cured at source). */
  readonly buyerName: string;
  readonly onBuyerName: (value: string) => void;
  /** B415: the press-refusal for a missing name — rests quiet, arrives on PRESS (§10 / #8-r). */
  readonly nameRefusal?: string | undefined;
  readonly buyerEmail: string;
  readonly onBuyerEmail: (value: string) => void;
  /** B511: the email the wallet did not share — asked for at the field, before any order. */
  readonly emailRefusal?: string | undefined;
  /** B603 — the optional phone's refusal: only ever about a TYPED value. */
  readonly phoneRefusal?: string | undefined;
  /** B511: the wallets this device can serve — absent/empty keeps the card path alone, under its own label. */
  readonly wallets?: readonly string[] | undefined;
  /** B415 (d): optional, for service updates — never required, never refused. */
  readonly buyerPhone: string;
  readonly onBuyerPhone: (value: string) => void;
  /** B415 (a): CLEAR CART releases the hold NOW and empties everything. */
  readonly onClearCart?: (() => void) | undefined;
  readonly gatewayReady: boolean;
  /** Why the gateway is absent, when it is (fail-closed reason, named). */
  readonly gatewayNotice?: string | undefined;
  /** confirmPayment resolved ok; the kernel has not yet confirmed (B23b). */
  readonly authorized?: boolean;
  readonly paying: boolean;
  /** B31g: §4.2 hints in buyer language; any hint walls PAY with its reason. */
  readonly hints?: readonly { readonly lineIds: readonly string[]; readonly message: string }[];
  readonly onPay: () => void;
  /** B391 — the line's count is drivable; 0 routes to remove in the container. */
  readonly onSetQuantity?: (lineId: string, quantity: number) => void;
  /** R14 — the ghost Remove door the frame does not draw (recorded deviation). */
  readonly onRemove?: (lineId: string) => void;
  /** B338 — the cart the buyer is looking at, for per-door busy keys. */
  readonly cartId?: string | undefined;
  /** B338 — the ONE in-flight field: the key of the cart act in the air. */
  readonly cartBusy?: string | undefined;
  /** R14 — the way back to the catalog, cart surviving. */
  readonly onContinue?: () => void;
  /**
   * B523 — which half of the merged page this face is (the dialog walk
   * splits Cart from Pay): "merged" (default, 73:2 hosted page) · "cart"
   * (lines + promo + CONTINUE TO PAY — no buyer fields, 370-2 as drawn) ·
   * "pay" (356-2's order, B545 leg (1): the wallet row FIRST, then OR PAY
   * WITH CARD, CARD + the card field, NAME, EMAIL (phone after, a B415 (d)
   * recorded deviation) — the buyer attaches at Pay through order.buyer.set).
   */
  readonly step?: "merged" | "cart" | "pay";
  /** B523 — the Cart step's Continue IS cart.checkout (hold + timer begin). */
  readonly onCheckout?: () => void;
  /** B523 — the availability re-read is still in the air: Continue waits. */
  readonly checking?: boolean;
  /** B523 (370-2) — Edit: back to the line's product with the line loaded. */
  readonly onEditLine?: (lineId: string) => void;
  /** B545 (e) — the sheet reopened over a live hold and resumed HERE: say so. */
  readonly resumed?: boolean;
  /** B545 (e) — ms left on the resumed hold (absent = hold-free: no clock). */
  readonly resumedMsLeft?: number | undefined;
  /** B545 (e) — Start over: abandons the pending order (seats free now) and opens a fresh cart. */
  readonly onStartOver?: (() => void) | undefined;
  /** B545 (1) — 356-2's trust line opens with "Test mode" ONLY while the channel's key is a test key. */
  readonly testMode?: boolean | undefined;
  /** B615 leg 2 — the ONE line the cart's policy facts compose; absent = nothing to agree to, no box. */
  readonly acknowledgement?: string | undefined;
  readonly acknowledged?: boolean;
  readonly onAcknowledged?: (ticked: boolean) => void;
  /** B615 leg 2 — the press-refusal at the box (rests quiet, arrives on PAY). */
  readonly acknowledgeRefusal?: string | undefined;
  /** B616 leg 3a — the Insider opt-in's words; absent = not asked, no box. Never required. */
  readonly optIn?: string | undefined;
  readonly optedIn?: boolean;
  readonly onOptIn?: (ticked: boolean) => void;
  /** B614 (c) — a SHOW tenant's cart opens with each show-night it holds (555:78); absent = no heads. */
  readonly showHeads?: readonly ShowHeadFace[];
}): ReactElement {
  const step = props.step ?? "merged";
  /* B523 (370-2) — the cart foot's number: the kernel's total once known,
     else the lines' own sum (every amount present and one currency — a
     partial sum would be a guess, so it stays absent instead, §10). */
  const summed = ((): Money | undefined => {
    if (props.total !== undefined) return props.total;
    if (props.lines.length === 0 || props.lines.some((l) => l.amount === undefined)) return undefined;
    const currency = props.lines[0]!.amount!.currency;
    if (props.lines.some((l) => l.amount!.currency !== currency)) return undefined;
    return { amount: props.lines.reduce((n, l) => n + l.amount!.amount, 0), currency };
  })();
  const ticketCount = props.lines.reduce((n, l) => n + l.quantity, 0);
  const [promoCode, setPromoCode] = useState("");
  const hold = props.holdMsLeft === undefined ? undefined : holdWords(props.holdMsLeft);
  const hints = props.hints ?? [];
  const hintsFor = (lineId: string): readonly string[] =>
    hints.filter((h) => h.lineIds.includes(lineId)).map((h) => h.message);
  /* B338 — ONE region for the cart's act (single-valued by construction). */
  const working = busyWords(props.cartBusy);

  if (props.lines.length === 0) {
    /* An empty cart pays for nothing: the teaching empty face stands alone —
       no card fields, no PAY (§10: the absence IS the face). */
    return (
      <div className="cartpay">
        <SheetCard redline="cartpay-cart" className="cartpay-cart">
          <EmptyState
            title="Your cart is empty"
            body="Tickets you add will appear here."
            action={
              props.onContinue !== undefined ? (
                <Button variant="neutral" onClick={props.onContinue}>
                  Browse tickets
                </Button>
              ) : undefined
            }
          />
        </SheetCard>
      </div>
    );
  }

  if (step === "cart") return <SheetCart {...props} summed={summed} ticketCount={ticketCount} />;

  /* B296 — why PAY is shut, in the buyer's words, ONE statement (B285). */
  const payWall =
    hints.length > 0
      ? "Adjust your tickets to pay."
      : !props.gatewayReady
        ? (props.gatewayNotice ??
          "Card payment isn’t set up on this booking page yet. Ask the seller how to pay — nothing has been charged.")
        : undefined;

  /* B805 (555:78) — a SHOW night on the merged page: on a wide face the
     page is the frame's two columns (styles.css "B805"). The dialog's steps
     keep their own grid (B545). */
  const showRecipe = step === "merged" && props.showHeads !== undefined && props.showHeads.length > 0;

  return (
    <div className={showRecipe ? "cartpay cartpay--show" : "cartpay"}>
      {props.onContinue !== undefined ? (
        <div className="back">
          <Button variant="ghost" onClick={props.onContinue}>
            ‹ Keep shopping
          </Button>
        </div>
      ) : null}

      {step === "pay" ? null : (
      <SheetCard redline="cartpay-cart" className="cartpay-cart">
        {props.showHeads !== undefined && props.showHeads.length > 0 ? (
          <ul className="cartpay-shows">
            {props.showHeads.map((h) => (
              <li key={h.key}>
                <ShowSummary
                  title={h.title}
                  {...(h.when !== undefined ? { when: h.when } : {})}
                  {...(h.place !== undefined ? { place: h.place } : {})}
                  {...(h.image !== undefined ? { image: h.image } : {})}
                  redline="cartpay-show"
                />
              </li>
            ))}
          </ul>
        ) : null}
        <header className="cartpay-cart-head">
          <h2 className="cartpay-label">Your cart</h2>
          {/* B415 (a): the caps ACCENT nav-link register (73:2 amendment —
              MANAGE →'s family, Rajdhani SemiBold 11 ls3 #5b9bff; B403 #33) —
              never a button. Clearing RELEASES THE HOLD immediately; the
              authorized face retires it (money in flight, nothing may release). */}
          {props.onClearCart !== undefined && props.authorized !== true ? (
            <Button variant="textlink-accent" onClick={props.onClearCart}>
              Clear cart
            </Button>
          ) : null}
          {hold !== undefined ? (
            <span className="cartpay-hold" role="status">
              <span className="cartpay-hold-dot" aria-hidden="true" />
              {hold}
            </span>
          ) : null}
        </header>

        {working !== undefined ? (
          <p className="cartpay-working" role="status">
            {working}
          </p>
        ) : null}
        <ul className="cartpay-lines">
          {props.lines.map((line) => (
            <li key={line.lineId} className="cartpay-line">
              <span className="cartpay-line-sentence">{line.sentence}</span>
              {props.onSetQuantity !== undefined ? (
                /* B391 — floor 1, the mark AT the floor is Remove; no shut
                   face in flight (B299: set_quantity is idempotent by value). */
                <UiStepper
                  frame="sheet"
                  label={line.typeName}
                  value={line.quantity}
                  min={1}
                  max={99}
                  onChange={(next) => props.onSetQuantity?.(line.lineId, next)}
                  onMinDecrease={() => props.onSetQuantity?.(line.lineId, 0)}
                  decreaseLabel={`Fewer ${line.typeName}`}
                  increaseLabel={`More ${line.typeName}`}
                  minDecreaseLabel={`Remove ${line.typeName}`}
                />
              ) : null}
              {line.amount !== undefined ? <span className="cartpay-line-amount">{formatMoney(line.amount)}</span> : null}
              {props.onEditLine !== undefined ? (
                /* B523 (370-2) — Edit walks back to the product's day & time
                   with this line's picks loaded (the container owns the walk). */
                <Button variant="ghost" ariaLabel={`Edit ${line.sentence}`} onClick={() => props.onEditLine?.(line.lineId)}>
                  Edit
                </Button>
              ) : null}
              {props.onRemove !== undefined ? (
                <Button
                  variant="ghost"
                  ariaLabel={`Remove ${line.sentence}`}
                  busy={doorBusy(props.cartBusy, cartActKey("cart.remove", props.cartId, line.lineId))}
                  onClick={() => props.onRemove?.(line.lineId)}
                >
                  Remove
                </Button>
              ) : null}
              {hintsFor(line.lineId).map((message) => (
                /* B31g/B223 — the hint renders AT the offending line, on the package Banner. */
                <div key={message}>
                  <Banner tone="danger">{message}</Banner> {/* wayback-ok: the line's own stepper beside it is the way back — the hint clears when the cart is right */}
                </div>
              ))}
            </li>
          ))}
        </ul>

        {!props.promoApplied ? (
          <form
            className="cartpay-promo"
            onSubmit={(e) => {
              e.preventDefault();
              /* B312 — the press always reaches the one guard. */
              props.onApplyPromo(promoCode);
            }}
          >
            {/* The frame draws the field placeholder-only; the visible label
                stays because the hidden-label budget is shrink-only (B313) —
                a recorded deviation the design side may overrule. */}
            {/* width-ok: 73:2's promo row — input 241 + APPLY 69, gap 12 (`.cartpay-promo`'s grid owns it). */}
            <Input id="promo-code" label="Promo code" value={promoCode} placeholder="Promo code" onChange={setPromoCode} error={props.promoRefusal} />
            <Button
              type="submit"
              variant="neutral"
              busy={doorBusy(props.cartBusy, cartActKey("cart.apply_promo", props.cartId))}
              onClick={() => undefined}
            >
              Apply
            </Button>
          </form>
        ) : null}
        {props.promoApplied ? <p className="cartpay-promo-applied">Promo applied.</p> : null}
        {(props.adjustments ?? []).map((a) => (
          /* B170 (§10.2): the outcome is stated — name + signed amount. */
          <p key={a.promotionId} className="cartpay-adjustment">
            {a.label} <span className="cartpay-adjustment-amount">{formatMoney(a.amount)}</span>
          </p>
        ))}
        {(props.charges ?? []).map((c) => (
          /* B629 (c): a fee or the tax, named, before the Total. */
          <p key={c.key} className="cartpay-adjustment" data-redline="embed-cart-charge">
            {c.label} <span className="cartpay-adjustment-amount">{formatMoney(c.amount)}</span>
          </p>
        ))}

        {hints.length > 0 ? (
          <p className="cartpay-gate" role="status" id="cartpay-hints-gate">
            Adjust your tickets to pay.
          </p>
        ) : null}
        <div className="cartpay-total" data-redline="cartpay-total">
          <span className="cartpay-total-word">Total</span>
          {props.total !== undefined ? <span className="cartpay-total-amount">{formatMoney(props.total)}</span> : null}
        </div>
      </SheetCard>
      )}

      <SheetCard redline="cartpay-pay" className="cartpay-pay">
        {step === "pay" && props.resumed === true && props.authorized !== true ? (
          /* B545 (e) — reopened over a live hold: the buyer is told where
             they are, and the way out is ONE text link (it releases the
             seats now and opens a fresh cart). */
          <div className="cartpay-resumed" data-redline="cartpay-resumed">
            <p className="cartpay-resumed-line" role="status">
              {resumedWords(props.resumedMsLeft)}
            </p>
            {props.onStartOver !== undefined ? (
              <Button variant="textlink-accent" onClick={props.onStartOver}>
                Start over
              </Button>
            ) : null}
          </div>
        ) : null}
        {step === "pay" && props.authorized !== true ? (
          <PayBlock {...props} />
        ) : null}
        {step === "pay" || props.authorized === true ? null : (
          /* B511 (356-2's shape on 73:2) — wallet FIRST: the wallets' own
             native buttons mount here; one press = the order. */
          <div id="express-checkout-element" className="cartpay-wallets" hidden={props.wallets === undefined || props.wallets.length === 0} />
        )}
        {step === "pay" && props.authorized !== true ? null : props.authorized !== true && props.wallets !== undefined && props.wallets.length > 0 ? (
          /* With a wallet above, the divider IS the card path's label. */
          <LabelledRule label="or pay with card" />
        ) : (
          <h2 className="cartpay-label">Pay by card</h2>
        )}
        {step === "pay" && props.authorized !== true ? null : props.authorized === true ? (
          /* Honest state, not optimism: the provider holds the money, the
             order is not yet a purchase. No fields, no door — the poller
             upstairs moves us the moment the kernel does. */
          /* The WAIT face (Pathik 2026-10-13): the buyer's story walks — Processing → Hang tight → Creating your tickets — over breathing dots; the poller upstairs moves us the moment the kernel does. */
          <PaymentWait redline="pay-wait" />
        ) : (
          <>
            {/* RULING (Pathik, 2026-08-28): "the name/email should be above
                payment" — the identity block leads, the card fields follow.
                B415 (b): NAME above EMAIL — the tickets are booked under it.
                The field rests QUIET; the refusal arrives only on the pay
                press, through the error seam (no at-rest amber).
                B545 (d): the dialog's PAY step carries it too — Cart's
                Continue names nobody; the buyer attaches here (after the
                wallet row, which brings its own details). */}
            <Input
              id="buyer-name"
              label="Name for the booking"
              value={props.buyerName}
              placeholder="Maya Chen"
              onChange={props.onBuyerName}
              error={props.nameRefusal}
              redline="cartpay-pay-name"
            />
            <Input
              id="buyer-email"
              label="Email for your tickets"
              type="email"
              value={props.buyerEmail}
              placeholder="you@example.com"
              onChange={props.onBuyerEmail}
              error={props.emailRefusal}
              redline="cartpay-pay-email"
            />
            {/* B415 (d): optional — the label says so, and why we would use it. */}
            <Input
              id="buyer-phone"
              label="Phone — optional, for service updates"
              type="tel"
              value={props.buyerPhone}
              placeholder="+1 415 555 0128"
              onChange={props.onBuyerPhone}
              error={props.phoneRefusal}
            />
            <OptInBox {...props} />
            <AcknowledgeBox {...props} />
            {/* Stripe's deferred-intent element mounts here (§7 step 4: card
                fields render inside OUR origin; the frame's CARD NUMBER /
                EXPIRY / CVC / ZIP anatomy is the element's own). */}
            <div id="payment-element" />
            {props.gatewayReady ? null : (
              <Banner tone="info" id="pay-gate">
                {payWall}
              </Banner>
            )}
            <TrustFootnote gatewayReady={props.gatewayReady} held={hold !== undefined} />
          </>
        )}
      </SheetCard>

      {/* B636 — the Pay step keeps the SUMMARY beside the card form, so
          Cart and Pay share the 740/364 grid (the right column was blank). */}
      {step === "pay" ? (
        <CartSummaryCard
          lines={props.lines}
          promoApplied={props.promoApplied}
          onApplyPromo={props.onApplyPromo}
          {...(props.promoRefusal !== undefined ? { promoRefusal: props.promoRefusal } : {})}
          promoEditable={false}
          {...(props.cartBusy !== undefined ? { cartBusy: props.cartBusy } : {})}
          {...(props.cartId !== undefined ? { cartId: props.cartId } : {})}
          {...(props.adjustments !== undefined ? { adjustments: props.adjustments } : {})}
          {...(props.charges !== undefined ? { charges: props.charges } : {})}
          summed={summed}
          hints={hints}
        />
      ) : null}

      {props.authorized === true ? null : (
        <StickyBar
          redline="cartpay-sticky"
          seatedWhenWide
          summary={
            step === "pay" ? (
              /* 356:85 — the trust line; each clause only while it is true (§10). */
              <span data-redline="embed-pay-trust">
                {[...(props.testMode === true ? ["Test mode"] : []), ...(props.gatewayReady ? ["Secure checkout"] : []), "Powered by transact"].join(" · ")}
              </span>
            ) : (
              "Secure checkout · Powered by transact"
            )
          }
        >
          <Button
            size="lg"
            disabled={payWall !== undefined}
            busy={props.paying}
            {...(payWall !== undefined ? { describedBy: hints.length > 0 ? "cartpay-hints-gate" : "pay-gate" } : {})}
            onClick={props.onPay}
            {...(step === "pay" ? { data: { redline: "embed-pay-button" } } : {})}
          >
            {props.total !== undefined ? `Pay ${formatMoney(props.total)}` : "Pay"}
          </Button>
        </StickyBar>
      )}
    </div>
  );
}

/**
 * B545 (370-2 / 431-344 — redline-embed-cart(-light).json, every number the
 * frame's) — the DIALOG's Cart step. Left: one CartLineCard per line
 * (photo · name · when · qty × class + total · Edit / Remove — no stepper,
 * the frame's `uncovered`), then ADD ANOTHER TICKET. Right: the SUMMARY card
 * (promo, Subtotal, the fired promotions, Total WITH its figure — (c): the
 * label used to sit alone). Foot: total + count + CONTINUE TO PAY, which IS
 * cart.checkout (B523). No buyer fields here — they moved to Pay (B545 (d)).
 */
/**
 * B636 (370-2 / 555:78 — Pathik 2026-10-02: "alignment/width between
 * checkout steps is still not consistent") — the booking SUMMARY card,
 * shared by the Cart step (left: the line cards; right: this) and the Pay
 * step (left: the card form; right: this) so the two steps draw ONE grid
 * and the Pay step no longer leaves its right column blank. Promo line,
 * subtotal, the fired promotions, Total WITH its figure — the 31-element
 * embed-cart sidecar, unchanged.
 */
function CartSummaryCard(props: {
  readonly lines: readonly CartCardLine[];
  readonly promoApplied: boolean;
  readonly onApplyPromo: (code: string) => void;
  readonly promoRefusal?: string | undefined;
  readonly cartBusy?: string | undefined;
  readonly cartId?: string | undefined;
  /** B636 — the promo DOOR belongs to the Cart step only; the Pay step shows the
      applied note + sums, never the entry form (356-2 draws no promo on Pay). */
  readonly promoEditable?: boolean;
  readonly adjustments?: readonly CartAdjustmentFace[];
  readonly charges?: readonly ChargeRow[];
  readonly summed: Money | undefined;
  readonly hints: readonly { readonly lineIds: readonly string[]; readonly message: string }[];
}): ReactElement {
  const [promoCode, setPromoCode] = useState("");
  const hints = props.hints;
  /* Subtotal = the lines' own sum — every amount present and one currency,
     else absent (a partial sum is a guess, §10). */
  const subtotal = ((): Money | undefined => {
    if (props.lines.some((l) => l.amount === undefined)) return undefined;
    const currency = props.lines[0]?.amount?.currency;
    if (currency === undefined || props.lines.some((l) => l.amount!.currency !== currency)) return undefined;
    return { amount: props.lines.reduce((n, l) => n + l.amount!.amount, 0), currency };
  })();
  return (
    <SheetCard redline="embed-cart-summary" className="cartpay-summary">
        {!props.promoApplied && props.promoEditable !== false ? (
          <form
            className="cartpay-promo"
            onSubmit={(e) => {
              e.preventDefault();
              /* B312 — the press always reaches the one guard. */
              props.onApplyPromo(promoCode);
            }}
          >
            <Input
              /* width-ok: 370:2's Totals card — the field to x1048 (220), APPLY 88 beside it (`.cartpay-summary .cartpay-promo` owns it). */
              id="promo-code"
              label="Promo code"
              value={promoCode}
              placeholder="Promo code"
              onChange={setPromoCode}
              error={props.promoRefusal}
              redline="embed-cart-promo"
            />
            <Button
              type="submit"
              variant="neutral"
              busy={doorBusy(props.cartBusy, cartActKey("cart.apply_promo", props.cartId))}
              onClick={() => undefined}
              data={{ redline: "embed-cart-promo-apply" }}
            >
              Apply
            </Button>
          </form>
        ) : props.promoApplied ? (
          <p className="cartpay-promo-applied">Promo applied.</p>
        ) : null}        <dl className="cartpay-sums">
          {subtotal !== undefined ? (
            <div className="cartpay-sum">
              <dt data-redline="embed-cart-subtotal-label">Subtotal</dt>
              <dd>{formatMoney(subtotal)}</dd>
            </div>
          ) : null}
          {(props.adjustments ?? []).map((a) => (
            /* B170 (§10.2): the outcome is stated — name + signed amount. */
            <div key={a.promotionId} className="cartpay-sum cartpay-sum--discount" data-redline="embed-cart-discount">
              <dt>{a.label}</dt>
              <dd>{formatMoney(a.amount)}</dd>
            </div>
          ))}
          {(props.charges ?? []).map((c) => (
            /* B629 (c): each fee the buyer pays, then Tax — visible BEFORE the pay press. */
            <div key={c.key} className="cartpay-sum" data-redline="embed-cart-charge">
              <dt>{c.label}</dt>
              <dd>{formatMoney(c.amount)}</dd>
            </div>
          ))}
          <div className="cartpay-sum cartpay-sum--total">
            <dt data-redline="embed-cart-total-label">Total</dt>
            {props.summed !== undefined ? <dd data-redline="embed-cart-total">{formatMoney(props.summed)}</dd> : null}
          </div>
        </dl>
        {hints.length > 0 ? (
          <p className="cartpay-gate" role="status" id="cartpay-hints-gate">
            Adjust your tickets to pay.
          </p>
        ) : null}
    </SheetCard>
  );
}

function SheetCart(
  props: Parameters<typeof CartPayView>[0] & { readonly summed: Money | undefined; readonly ticketCount: number },
): ReactElement {
  const hints = props.hints ?? [];
  const hintsFor = (lineId: string): readonly string[] => hints.filter((h) => h.lineIds.includes(lineId)).map((h) => h.message);
  const working = busyWords(props.cartBusy);
  return (
    <div className="cartpay cartpay--sheet">
      <div className="cartpay-linescol">
        {working !== undefined ? (
          <p className="cartpay-working" role="status">
            {working}
          </p>
        ) : null}
        {props.lines.map((line, i) => (
          <CartLineCard
            key={line.lineId}
            redline={`embed-cart-line-${i + 1}`}
            name={line.productName ?? line.typeName}
            when={line.when}
            qty={line.qtyWords ?? line.sentence}
            total={line.amount !== undefined ? formatMoney(line.amount) : undefined}
            photo={line.photo}
            {...(props.onEditLine !== undefined ? { onEdit: () => props.onEditLine?.(line.lineId), editLabel: `Edit ${line.sentence}` } : {})}
            {...(props.onRemove !== undefined
              ? {
                  onRemove: () => props.onRemove?.(line.lineId),
                  removeLabel: `Remove ${line.sentence}`,
                  removeBusy: doorBusy(props.cartBusy, cartActKey("cart.remove", props.cartId, line.lineId)),
                }
              : {})}
          >
            {hintsFor(line.lineId).map((message) => (
              /* B31g/B223 — the hint renders AT the offending line, on the package Banner. */
              <div key={message}>
                <Banner tone="danger">{message}</Banner> {/* wayback-ok: the line's own Edit/Remove beside it is the way back — the hint clears when the cart is right */}
              </div>
            ))}
          </CartLineCard>
        ))}
        {props.onContinue !== undefined ? (
          <div className="cartpay-add-another">
            <Button variant="neutral" onClick={props.onContinue} data={{ redline: "embed-cart-add" }}>
              <span data-redline="embed-cart-add-label">+ Add another ticket</span>
            </Button>
          </div>
        ) : null}
      </div>

      <CartSummaryCard
        lines={props.lines}
        promoApplied={props.promoApplied}
        onApplyPromo={props.onApplyPromo}
        {...(props.promoRefusal !== undefined ? { promoRefusal: props.promoRefusal } : {})}
        {...(props.cartBusy !== undefined ? { cartBusy: props.cartBusy } : {})}
        {...(props.cartId !== undefined ? { cartId: props.cartId } : {})}
        {...(props.adjustments !== undefined ? { adjustments: props.adjustments } : {})}
          {...(props.charges !== undefined ? { charges: props.charges } : {})}
        summed={props.summed}
        hints={hints}
      />

      <SheetFoot
        pinned
        redline="embed-sheet-footer"
        totalRedline="embed-sheet-footer-total"
        subRedline="embed-sheet-footer-count"
        {...(props.summed !== undefined ? { total: formatMoney(props.summed) } : {})}
        {...(props.ticketCount > 0 ? { sub: `${props.ticketCount} ticket${props.ticketCount === 1 ? "" : "s"}` } : {})}
      >
        <Button
          size="lg"
          disabled={hints.length > 0 || props.lines.length === 0 || props.checking === true}
          busy={props.paying}
          {...(hints.length > 0 ? { describedBy: "cartpay-hints-gate" } : {})}
          onClick={() => props.onCheckout?.()}
          data={{ redline: "embed-sheet-continue" }}
        >
          <span data-redline="embed-sheet-continue-label">Continue to pay →</span>
        </Button>
      </SheetFoot>
    </div>
  );
}

/**
 * B545 leg (1) — the dialog's PAY step drawn to 356-2 (redline-embed-pay.json):
 * wallet row → OR PAY WITH CARD (only with a wallet above) → CARD + the
 * provider's card field → NAME FOR THE BOOKING → EMAIL FOR YOUR TICKETS, 18
 * between blocks. The frame supersedes the 2026-08-28 "name/email above
 * payment" order for this step (356-2 was drawn 2026-09-22 on the wallet
 * ruling; the widget — the same frame's own face — already reads card first);
 * reversal = move the card pair below the email again. PHONE stays after the
 * email: B415 (d)'s ruled optional field, a recorded deviation the frame does
 * not draw. No trust FOOTNOTE here — 356-2 draws only the trust line under PAY.
 */
function PayBlock(props: Parameters<typeof CartPayView>[0]): ReactElement {
  const wallets = props.wallets !== undefined && props.wallets.length > 0;
  /* The same ONE statement the sticky PAY is walled on (B296), shown where
     the card field would be only while the gateway is absent. */
  const payWall = props.gatewayReady
    ? undefined
    : (props.hints ?? []).length > 0
      ? "Adjust your tickets to pay."
      : (props.gatewayNotice ??
        "Card payment isn’t set up on this booking page yet. Ask the seller how to pay — nothing has been charged.");
  return (
    <div className="cartpay-payblock" data-redline="embed-pay-block">
      {/* B511 — the wallets' own native buttons mount here; one press = the order. */}
      <div id="express-checkout-element" className="cartpay-wallets" data-redline="embed-pay-wallets" hidden={!wallets} />
      {wallets ? <LabelledRule label="or pay with card" redline="embed-pay-rule" /> : null}
      <div className="cartpay-cardfield">
        <p className="cartpay-label" id="cartpay-card-label" data-redline="embed-pay-card-label">
          Card
        </p>
        {/* Stripe's deferred-intent element (§7 step 4: the fields render
            inside OUR origin; `Card number MM / YY CVC` is the element's own). */}
        <div id="payment-element" data-redline="embed-pay-card" aria-labelledby="cartpay-card-label" />
      </div>
      {payWall === undefined ? null : (
        <Banner tone="info" id="pay-gate">
          {payWall}
        </Banner>
      )}
      {/* B415 (b): NAME above EMAIL — the tickets are booked under it. Rests
          QUIET; the refusal arrives on the PAY press through the error seam. */}
      <Input
        id="buyer-name"
        label="Name for the booking"
        value={props.buyerName}
        placeholder="Maya Chen"
        onChange={props.onBuyerName}
        error={props.nameRefusal}
        redline="embed-pay-name"
      />
      <Input
        id="buyer-email"
        label="Email for your tickets"
        type="email"
        value={props.buyerEmail}
        placeholder="you@example.com"
        onChange={props.onBuyerEmail}
        error={props.emailRefusal}
        redline="embed-pay-email"
      />
      <Input
        id="buyer-phone"
        label="Phone — optional, for service updates"
        type="tel"
        value={props.buyerPhone}
        placeholder="+1 415 555 0128"
        onChange={props.onBuyerPhone}
        error={props.phoneRefusal}
      />
      <OptInBox {...props} />
      <AcknowledgeBox {...props} />
    </div>
  );
}

/**
 * B616 leg 3a — the Insider opt-in (555:78): ONE real checkbox, unticked
 * until the buyer ticks it and never a reason to refuse PAY. It sits after
 * the buyer's details and before the acknowledgement, which stays last.
 */
function OptInBox(props: Parameters<typeof CartPayView>[0]): ReactNode {
  if (props.optIn === undefined) return null;
  return <Checkbox id="buyer-optin" label={props.optIn} checked={props.optedIn === true} onChange={(ticked) => props.onOptIn?.(ticked)} />;
}

/**
 * B615 leg 2 — the checkout acknowledgement (555:78): ONE real checkbox whose
 * label is the line the cart's policy facts compose ("I’m 21 or older and
 * understand all sales are final."). Last before PAY, after the buyer's
 * details; the refusal arrives on the press through the field's error seam.
 * No facts that ask for agreement → nothing drawn.
 */
function AcknowledgeBox(props: Parameters<typeof CartPayView>[0]): ReactNode {
  if (props.acknowledgement === undefined) return null;
  return (
    <Checkbox
      id="buyer-acknowledge"
      label={props.acknowledgement}
      checked={props.acknowledged === true}
      onChange={(ticked) => props.onAcknowledged?.(ticked)}
      {...(props.acknowledgeRefusal !== undefined ? { error: props.acknowledgeRefusal } : {})}
    />
  );
}

/** B545 (e) — "Your tickets are still held · 7:12 left" (no clock on a hold-free order). */
export function resumedWords(msLeft: number | undefined): string {
  if (msLeft === undefined || !Number.isFinite(msLeft) || msLeft <= 0) return "Your tickets are still held.";
  const total = Math.floor(msLeft / 1000);
  return `Your tickets are still held · ${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")} left`;
}

/**
 * 73:2's trust footnote — BOTH clauses are CLAIMS, each rendered only
 * while it is true of the integration: tokenised fields (the provider's
 * element is the thing on the page) and an active hold. No claim, no
 * sentence — a trust line that can be false is worse than none (§10).
 */
function TrustFootnote(props: { readonly gatewayReady: boolean; readonly held: boolean }): ReactNode {
  const clauses: string[] = [];
  if (props.gatewayReady) clauses.push("Card details go straight to the payment provider — never through us.");
  if (props.held) clauses.push("Your seats stay held until the timer runs out.");
  if (clauses.length === 0) return null;
  return <p className="cartpay-trust">{clauses.join(" ")}</p>;
}
